import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { X, Minus, Plus, RotateCcw } from "lucide-react";

import type { IndicatorInstance, IndicatorFigureStyle } from "@/types/indicator";
import { getCatalogEntry, type IndicatorCatalogEntry } from "@/lib/lytick/indicatorRegistry";
import { useChartSettings } from "@/hooks/useChartSettings";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

interface IndicatorSettingsProps {
  instance: IndicatorInstance;
  open: boolean;
  onClose: () => void;
}

export function IndicatorSettings({ instance, open, onClose }: IndicatorSettingsProps) {
  const catalog = getCatalogEntry(instance.name);
  const { updateIndicatorById } = useChartSettings();

  if (!catalog) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[480px] p-0 gap-0" style={{ backgroundColor: "var(--bg-surface)", borderColor: "var(--border-default)" }}>
        <DialogHeader className="px-4 pt-4 pb-2 border-b" style={{ borderColor: "var(--border-default)" }}>
          <DialogTitle className="text-sm font-semibold flex items-center justify-between" style={{ color: "var(--text-primary)" }}>
            {catalog.displayName} Settings
            <button onClick={onClose} className="inline-flex items-center justify-center rounded-md h-6 w-6" style={{ color: "var(--text-secondary)" }}>
              <X className="h-3.5 w-3.5" />
            </button>
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="inputs" className="flex-1">
          <div className="px-4 pt-2">
            <TabsList className="w-full">
              <TabsTrigger value="inputs" className="flex-1 text-xs">Inputs</TabsTrigger>
              <TabsTrigger value="style" className="flex-1 text-xs">Style</TabsTrigger>
              <TabsTrigger value="visibility" className="flex-1 text-xs">Visibility</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="inputs" className="px-4 py-3 overflow-y-auto max-h-[50vh]">
            <InputsTab instance={instance} catalog={catalog} onUpdate={updateIndicatorById} />
          </TabsContent>

          <TabsContent value="style" className="px-4 py-3 overflow-y-auto max-h-[50vh]">
            <StyleTab instance={instance} catalog={catalog} onUpdate={updateIndicatorById} />
          </TabsContent>

          <TabsContent value="visibility" className="px-4 py-3 overflow-y-auto max-h-[50vh]">
            <VisibilityTab instance={instance} catalog={catalog} onUpdate={updateIndicatorById} />
          </TabsContent>
        </Tabs>

        <div className="px-4 py-3 border-t flex justify-end" style={{ borderColor: "var(--border-default)" }}>
          <ResetButton instance={instance} catalog={catalog} onUpdate={updateIndicatorById} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Inputs Tab ──────────────────────────────────────────────────────────

function InputsTab({
  instance,
  catalog,
  onUpdate,
}: {
  instance: IndicatorInstance;
  catalog: IndicatorCatalogEntry;
  onUpdate: (id: string, patch: Partial<IndicatorInstance>) => void;
}) {
  const hasParams = catalog.calcParamLabels.length > 0;
  const hasSource = catalog.defaultSource !== undefined;

  return (
    <div className="space-y-3">
      {/* Nickname field */}
      <div className="flex items-center justify-between gap-3 min-h-[44px]">
        <span className="text-xs font-medium" style={{ color: "var(--text-secondary)" }}>
          Label
        </span>
        <input
          type="text"
          value={instance.nickname || ""}
          onChange={(e) => onUpdate(instance.id, { nickname: e.target.value || undefined })}
          placeholder={catalog.shortName}
          className="w-32 h-8 text-center text-xs rounded-md border"
          style={{
            backgroundColor: "var(--bg-elevated)",
            borderColor: "var(--border-default)",
            color: "var(--text-primary)",
          }}
        />
      </div>

      {/* Source dropdown */}
      {hasSource && (
        <div className="flex items-center justify-between gap-3 min-h-[44px]">
          <span className="text-xs font-medium" style={{ color: "var(--text-secondary)" }}>
            Source
          </span>
          <select
            value={instance.source || catalog.defaultSource || 'close'}
            onChange={(e) => onUpdate(instance.id, { source: e.target.value as any })}
            className="w-28 h-8 text-center text-xs rounded-md border"
            style={{
              backgroundColor: "var(--bg-elevated)",
              borderColor: "var(--border-default)",
              color: "var(--text-primary)",
            }}
          >
            <option value="close">Close</option>
            <option value="open">Open</option>
            <option value="high">High</option>
            <option value="low">Low</option>
            <option value="hl2">HL2</option>
            <option value="hlc3">HLC3</option>
            <option value="ohlc4">OHLC4</option>
          </select>
        </div>
      )}

      {!hasParams && !hasSource ? (
        <p className="text-xs text-center py-4" style={{ color: "var(--text-muted)" }}>
          No configurable parameters
        </p>
      ) : (
        catalog.calcParamLabels.map((label, i) => (
          <ParamControl
            key={label}
            label={label}
            type={catalog.calcParamTypes[i]}
            range={catalog.calcParamRanges?.[i]}
            value={instance.calcParams[i] as number}
            onChange={(val) => {
              const newParams = [...instance.calcParams];
              newParams[i] = val;
              onUpdate(instance.id, { calcParams: newParams });
            }}
          />
        ))
      )}
    </div>
  );
}

function ParamControl({
  label,
  type,
  range,
  value,
  onChange,
}: {
  label: string;
  type: string;
  range?: { min: number; max: number; step: number };
  value: number;
  onChange: (val: number) => void;
}) {
  const step = type === "multiplier" ? 0.1 : (range?.step ?? 1);
  const min = range?.min ?? 1;
  const max = range?.max ?? 9999;

  const decrement = () => onChange(Math.max(min, Math.round((value - step) * 1000) / 1000));
  const increment = () => onChange(Math.min(max, Math.round((value + step) * 1000) / 1000));

  return (
    <div className="flex items-center justify-between gap-3 min-h-[44px]">
      <span className="text-xs font-medium" style={{ color: "var(--text-secondary)" }}>
        {label}
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={decrement}
          className="inline-flex items-center justify-center rounded-md h-8 w-8"
          style={{ backgroundColor: "var(--bg-elevated)", color: "var(--text-secondary)" }}
        >
          <Minus className="h-3 w-3" />
        </button>
        <input
          type="number"
          value={value}
          step={step}
          min={min}
          max={max}
          onChange={(e) => {
            const v = parseFloat(e.target.value);
            if (!isNaN(v)) onChange(Math.max(min, Math.min(max, v)));
          }}
          className="w-16 h-8 text-center text-xs rounded-md border"
          style={{
            backgroundColor: "var(--bg-elevated)",
            borderColor: "var(--border-default)",
            color: "var(--text-primary)",
          }}
        />
        <button
          onClick={increment}
          className="inline-flex items-center justify-center rounded-md h-8 w-8"
          style={{ backgroundColor: "var(--bg-elevated)", color: "var(--text-secondary)" }}
        >
          <Plus className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

// ── Style Tab ───────────────────────────────────────────────────────────

function StyleTab({
  instance,
  catalog,
  onUpdate,
}: {
  instance: IndicatorInstance;
  catalog: IndicatorCatalogEntry;
  onUpdate: (id: string, patch: Partial<IndicatorInstance>) => void;
}) {
  const figureKeys = Object.keys(catalog.figureLabels);

  if (figureKeys.length === 0) {
    return (
      <p className="text-xs text-center py-4" style={{ color: "var(--text-muted)" }}>
        No style options
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {figureKeys.map((key) => (
        <FigureStyleControl
          key={key}
          figureKey={key}
          label={catalog.figureLabels[key]}
          style={instance.figureStyles[key] || {}}
          onChange={(newStyle) => {
            const newStyles = {
              ...instance.figureStyles,
              [key]: { ...instance.figureStyles[key], ...newStyle },
            };
            onUpdate(instance.id, { figureStyles: newStyles });
          }}
        />
      ))}
    </div>
  );
}

function FigureStyleControl({
  figureKey,
  label,
  style,
  onChange,
}: {
  figureKey: string;
  label: string;
  style: IndicatorFigureStyle;
  onChange: (patch: Partial<IndicatorFigureStyle>) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [pickerOpen]);

  return (
    <div className="space-y-2">
      <span className="text-xs font-medium" style={{ color: "var(--text-secondary)" }}>{label}</span>
      <div className="flex items-center gap-3">
        {/* Color swatch */}
        <div ref={pickerRef} className="relative">
          <button
            onClick={() => setPickerOpen((v) => !v)}
            className="h-8 w-8 rounded-md border"
            style={{
              backgroundColor: style.color || "#787B86",
              borderColor: pickerOpen ? "var(--accent-primary)" : "var(--border-default)",
            }}
            aria-label={`Pick color for ${label}`}
            aria-expanded={pickerOpen}
          />
          {pickerOpen && (
            <div className="absolute z-50 top-10 left-0">
              <HexColorPicker
                color={style.color || "#787B86"}
                onChange={(color) => onChange({ color })}
              />
            </div>
          )}
        </div>

        {/* Line style */}
        <div className="flex items-center gap-1">
          {(["solid", "dashed", "dotted"] as const).map((ls) => (
            <button
              key={ls}
              onClick={() => onChange({ lineStyle: ls })}
              className="h-8 w-8 rounded-md border flex items-center justify-center"
              style={{
                borderColor: style.lineStyle === ls ? "var(--accent-primary)" : "var(--border-default)",
                backgroundColor: style.lineStyle === ls ? "color-mix(in srgb, var(--accent-primary) 12%, transparent)" : "transparent",
              }}
              aria-label={`${ls} line`}
            >
              <div
                className="w-4"
                style={{
                  borderTop: `2px ${ls === "dotted" ? "dotted" : ls === "dashed" ? "dashed" : "solid"} ${style.color || "#787B86"}`,
                }}
              />
            </button>
          ))}
        </div>

        {/* Thickness */}
        <div className="flex items-center gap-0.5">
          {[1, 2, 3, 4].map((w) => (
            <button
              key={w}
              onClick={() => onChange({ lineWidth: w })}
              className="h-8 w-7 rounded-md border flex items-center justify-center"
              style={{
                borderColor: style.lineWidth === w ? "var(--accent-primary)" : "var(--border-default)",
                backgroundColor: style.lineWidth === w ? "color-mix(in srgb, var(--accent-primary) 12%, transparent)" : "transparent",
              }}
              aria-label={`Thickness ${w}`}
            >
              <div
                style={{
                  width: 12,
                  height: w,
                  backgroundColor: style.color || "#787B86",
                  borderRadius: 1,
                }}
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Visibility Tab ──────────────────────────────────────────────────────

function VisibilityTab({
  instance,
  catalog,
  onUpdate,
}: {
  instance: IndicatorInstance;
  catalog: IndicatorCatalogEntry;
  onUpdate: (id: string, patch: Partial<IndicatorInstance>) => void;
}) {
  const figureKeys = Object.keys(catalog.figureLabels);

  if (figureKeys.length === 0) {
    return (
      <p className="text-xs text-center py-4" style={{ color: "var(--text-muted)" }}>
        No visibility options
      </p>
    );
  }

  return (
    <div className="space-y-1">
      {figureKeys.map((key) => {
        const currentStyle = instance.figureStyles[key];
        const isVisible = currentStyle?.visible !== false;
        return (
          <button
            key={key}
            onClick={() => {
              const newStyles = {
                ...instance.figureStyles,
                [key]: { ...instance.figureStyles[key], visible: !isVisible },
              };
              onUpdate(instance.id, { figureStyles: newStyles });
            }}
            className="flex items-center justify-between w-full min-h-[44px] px-3 py-2 rounded-md transition-colors"
            style={{ backgroundColor: "transparent" }}
          >
            <span className="text-xs font-medium" style={{ color: "var(--text-primary)" }}>
              {catalog.figureLabels[key]}
            </span>
            <div
              className="h-5 w-8 rounded-full relative transition-colors"
              style={{ backgroundColor: isVisible ? "var(--accent-primary)" : "var(--bg-elevated)" }}
            >
              <div
                className="absolute top-0.5 h-4 w-4 rounded-full transition-transform"
                style={{
                  backgroundColor: "white",
                  left: isVisible ? 14 : 2,
                }}
              />
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ── Reset Button ────────────────────────────────────────────────────────

function ResetButton({
  instance,
  catalog,
  onUpdate,
}: {
  instance: IndicatorInstance;
  catalog: IndicatorCatalogEntry;
  onUpdate: (id: string, patch: Partial<IndicatorInstance>) => void;
}) {
  return (
    <button
      onClick={() => {
        onUpdate(instance.id, {
          calcParams: [...catalog.defaultCalcParams],
          source: catalog.defaultSource,
          figureStyles: Object.fromEntries(
            Object.entries(catalog.defaultFigureStyles).map(([key, style]) => [key, { ...style }]),
          ),
          nickname: undefined,
        });
      }}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
      style={{ color: "var(--text-secondary)" }}
    >
      <RotateCcw className="h-3 w-3" />
      Reset to Defaults
    </button>
  );
}
